import React, { useEffect, useState } from 'react';
import {
  buildEmailDomainSuggestions,
  filterEmailDomainSuggestions,
  getEmailDomainNames,
} from './emailDomainSuggestions.util';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import FormLabel from '@mui/material/FormLabel';
import Popper from '@mui/material/Popper';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import InfoIcon from '@mui/icons-material/Info';
import { Field } from '@sitecore-jss/sitecore-jss-nextjs';
import { IItemWithString } from 'lib/type/common';
import { ControllerRenderProps, FieldValues } from 'react-hook-form';

/** Forms that include an `email` field (narrow `useForm<{ email: string }>()` etc.). */
export type EmailFieldProps = ControllerRenderProps<FieldValues & { email?: string }, 'email'>;

export interface EmailAutocompleteProps {
  emailDomains: IItemWithString[];
  fieldProps: EmailFieldProps;
  /** Sitecore label above the field (registration, update-email). Omit or empty for placeholder-only (support). */
  emailTitle?: Field<string>;
  /** Native input placeholder (support form). Omit for registration-style field without placeholder. */
  placeholder?: string;
  inputId?: string;
  /** Neutral label + outlined field for light surfaces (support form, modals). */
  onLightBackground?: boolean;
  /**
   * When true, suggestion rows use dark body text (`neutral9`) to pair with the support email
   * field’s light placeholder (`neutral11`). Intended for {@link SupportForm} only.
   */
  matchListStyleToSupportInput?: boolean;
  /** Placeholder `::placeholder` color when `placeholder` is set (e.g. SupportForm design token). */
  placeholderTextColor?: string;
  /**
   * Outlined field for the update-email modal: visible border on white surfaces (8px radius, light outline).
   * Registration omits this (dark page + borderless pill); SupportForm omits this (uses `placeholder` / support chrome).
   */
  updateEmailDialogField?: boolean;
}

const EmailAutocomplete: React.FC<EmailAutocompleteProps> = (props) => {
  const {
    inputId = 'email',
    onLightBackground = false,
    placeholder = '',
    matchListStyleToSupportInput = false,
  } = props;
  const theme = useTheme();
  const [domainList, setDomainList] = useState<string[]>([]);
  const labelColor = onLightBackground ? theme.palette.neutral8.main : theme.palette.secondary.main;
  const showLabel = Boolean(String(props.emailTitle?.value ?? '').trim());
  const hasPlaceholder = Boolean(String(placeholder).trim());
  const updateEmailDialogField = Boolean(props.updateEmailDialogField);
  const identifySupportEmail = !updateEmailDialogField && (onLightBackground || hasPlaceholder);

  const popperOffsetY = parseFloat(theme.spacing(1.25)) || 10;
  const emailValue = String(props.fieldProps?.value ?? '');

  useEffect(() => {
    const doamins = getEmailDomainNames(props?.emailDomains);
    const newValue = props?.fieldProps?.value ? props?.fieldProps?.value : '';
    setDomainList(buildEmailDomainSuggestions(newValue, doamins));
  }, [props?.emailDomains, props?.fieldProps?.value]);

  const handleInputChange = (_event: React.SyntheticEvent, value: string) => {
    props?.fieldProps?.onChange(value ?? '');
  };

  const handleOnChange = (_event: React.SyntheticEvent, value: string | null) => {
    props?.fieldProps.onChange(value ?? '');
  };

  const filterOptions = (options: string[], { inputValue }: { inputValue: string }) => {
    const effectiveInput = (inputValue ?? '').trim() || emailValue;
    return filterEmailDomainSuggestions(options, effectiveInput);
  };

  const inputAriaLabel = hasPlaceholder ? placeholder : 'email';
  return (
    <Box sx={{ width: '100%' }}>
      {showLabel && (
        <FormLabel id={`${inputId}_label`} htmlFor={inputId}>
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{ color: labelColor, fontSize: theme.typography.body1.fontSize }}
          >
            {props?.emailTitle?.value}
            <Tooltip title={props.emailTitle?.value ?? ''}>
              <IconButton sx={{ color: labelColor, paddingLeft: theme.spacing(0) }}>
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </FormLabel>
      )}
      <Autocomplete
        freeSolo
        {...props.fieldProps}
        id={inputId}
        inputValue={emailValue}
        openOnFocus={domainList.length > 0}
        sx={{
          width: '100%',
          ...(updateEmailDialogField
            ? {}
            : identifySupportEmail
              ? {
                  borderRadius: theme.shape.borderRadius,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.neutral4.main,
                  },
                  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.neutral6.main,
                  },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                    borderWidth: theme.spacing(0.125),
                  },
                }
              : {
                  background: theme.palette.white.main,
                  borderRadius: 3,
                  '.MuiOutlinedInput-root': { padding: theme.spacing(0.375) },
                  '.MuiOutlinedInput-root .MuiAutocomplete-input': {
                    color: theme.palette.black.main,
                  },
                  '.MuiAutocomplete-popper': { background: theme.palette.white.main },
                }),
        }}
        disableClearable
        options={domainList}
        filterOptions={filterOptions}
        onInputChange={handleInputChange}
        onChange={handleOnChange}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              variant="outlined"
              fullWidth
              size={updateEmailDialogField ? 'small' : undefined}
              placeholder={hasPlaceholder ? placeholder : undefined}
              sx={{
                ...(updateEmailDialogField
                  ? {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: theme.palette.white.main,
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderRadius: '8px',
                        borderColor: theme.palette.neutral3.main,
                        borderWidth: 1,
                      },
                      '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.neutral4.main,
                      },
                      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                        borderWidth: 1,
                      },
                      '& .MuiInputBase-input, & .MuiOutlinedInput-root .MuiAutocomplete-input': {
                        color: theme.palette.black.main,
                        fontSize: theme.typography.body1.fontSize,
                        lineHeight: 1.43,
                      },
                    }
                  : identifySupportEmail
                    ? {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.neutral4.main,
                        },
                      }
                    : {
                        border: 'none',
                        '& fieldset': { border: 'none' },
                      }),
                ...(hasPlaceholder
                  ? {
                      /* Placeholder + value: align with SupportForm issue Select (body1 + neutral11). */
                      '& .MuiOutlinedInput-root': {
                        ...theme.typography.body1,
                      },
                      '& .MuiInputBase-input': {
                        color: '#757575',
                      },
                      '& input::placeholder': {
                        color: '#757575',
                      },
                      '& .MuiOutlinedInput-input::placeholder': {
                        color: '#000',
                      },
                      '& .MuiAutocomplete-input::placeholder': {
                        color: '#000',
                      },
                    }
                  : updateEmailDialogField
                    ? {}
                    : {
                        '& .MuiInputBase-input': {
                          fontSize: theme.typography.body1.fontSize,
                        },
                        '& .MuiInputBase-root, & .MuiOutlinedInput-root': {
                          fontSize: theme.typography.body1.fontSize,
                        },
                      }),
              }}
              InputProps={{
                ...params.InputProps,
                autoComplete: 'off',
                inputProps: {
                  ...params.inputProps,
                  'aria-label': inputAriaLabel,
                  'aria-required': true,
                  role: 'textbox',
                },
              }}
            />
          );
        }}
        renderOption={(optionProps, option) => (
          <Box
            component="li"
            {...optionProps}
            sx={
              matchListStyleToSupportInput
                ? {
                    ...theme.typography.body1,
                    color: theme.palette.neutral9.main,
                    fontWeight: theme.typography.fontWeightRegular,
                  }
                : { color: theme.palette.black.main }
            }
          >
            {option}
          </Box>
        )}
        PopperComponent={({ ...popperProps }) => (
          <Popper
            {...popperProps}
            style={{
              ...popperProps.style,
              zIndex: theme.zIndex.modal,
            }}
            modifiers={[
              {
                name: 'offset',
                options: {
                  offset: [0, popperOffsetY],
                },
              },
              {
                name: 'sameWidth',
                enabled: true,
                phase: 'beforeWrite',
                requires: ['computeStyles'],
                fn: ({ state }) => {
                  state.styles.popper.width = `${state.rects.reference.width}px`;
                },
                effect: ({ state }) => {
                  state.elements.popper.style.width = `${
                    state.elements.reference.getBoundingClientRect().width
                  }px`;
                },
              },
            ]}
          />
        )}
        noOptionsText=""
      />
    </Box>
  );
};

export default EmailAutocomplete;
